'use client';

import { Card, Label } from '@/components/ui';
import { DistributionGraph } from '../common/distribution-graph';
import { ScrollableContainer } from '../common/scrollable-container';
import type { AnovaControlValues } from './anova.config';

export interface AnovaResult {
  fStatistic: number;
  pValue: number;
  dfBetween: number;
  dfWithin: number;
  ssBetween: number;
  ssWithin: number;
  msBetween: number;
  msWithin: number;
  groupMeans: number[];
  groupSizes: number[];
  groups: number[][];
  significantDifference: boolean;
}

interface AnovaCalculatorProps {
  visualizationData: AnovaResult | null;
}

export function AnovaCalculator({ visualizationData: result }: AnovaCalculatorProps) {
  if (!result) {
    return null;
  }

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">ANOVA Results</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>F-Statistic</Label>
              <div>{result.fStatistic.toFixed(4)}</div>
            </div>
            <div>
              <Label>P-Value</Label>
              <div>{result.pValue.toFixed(4)}</div>
            </div>
            <div>
              <Label>Degrees of Freedom (Between)</Label>
              <div>{result.dfBetween}</div>
            </div>
            <div>
              <Label>Degrees of Freedom (Within)</Label>
              <div>{result.dfWithin}</div>
            </div>
            <div>
              <Label>Sum of Squares (Between)</Label>
              <div>{result.ssBetween.toFixed(4)}</div>
            </div>
            <div>
              <Label>Sum of Squares (Within)</Label>
              <div>{result.ssWithin.toFixed(4)}</div>
            </div>
            <div>
              <Label>Mean Square (Between)</Label>
              <div>{result.msBetween.toFixed(4)}</div>
            </div>
            <div>
              <Label>Mean Square (Within)</Label>
              <div>{result.msWithin.toFixed(4)}</div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <div className={result.significantDifference ? 'text-green-600' : 'text-yellow-600'}>
              {result.significantDifference
                ? 'There is a significant difference between groups'
                : 'No significant difference between groups'}
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Group Distributions</h3>
          {result.groups.map((groupData, index) => (
            <div key={index} className="mb-4">
              <Label>Group {index + 1}</Label>
              <div className="w-full">
                <DistributionGraph
                  data={groupData}
                  title={`Group ${index + 1} Distribution`}
                  showMean
                  showMedian
                  showStdDev
                  height={300}
                  binCount={15}
                  className="w-full"
                />
              </div>
            </div>
          ))}
        </Card>
      </div>
    </ScrollableContainer>
  );
} 